import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function TourViewPage() {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tour, setTour] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showInfo, setShowInfo] = useState(false);
    const [viewMode, setViewMode] = useState('tour'); // 'tour' or 'images'
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [defaultCurrency, setDefaultCurrency] = useState('USD');
    const [propertyTypes, setPropertyTypes] = useState([]);

    const viewRecorded = useRef(false);

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang); // This automatically updates localStorage
        localStorage.setItem('i18nextLngManual', 'true'); // Flag as manual choice
        const newParams = new URLSearchParams(searchParams);
        newParams.set('lang', lang);
        setSearchParams(newParams); // Update URL for sharing
    };



    useEffect(() => {
        const fetchTour = async () => {
            setLoading(true);
            setIframeLoaded(false);
            try {
                const res = await api.get(`/tours/${id}/public`);
                setTour(res.data);

                // Fetch default currency
                try {
                    const configRes = await api.get('/locations/config');
                    setDefaultCurrency(configRes.data.currency || 'USD');
                } catch (e) {
                    console.error("Failed to fetch default currency", e);
                }

                // Fetch property types for translation
                try {
                    const ptRes = await api.get('/locations/property-types');
                    setPropertyTypes(ptRes.data);
                } catch (e) {
                    console.error("Failed to fetch property types", e);
                }

                // Record view (only once)
                if (!viewRecorded.current) {
                    viewRecorded.current = true;
                    try {
                        await api.post(`/tours/${id}/view`);
                    } catch (e) {
                        console.error("Failed to increment view count", e);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch tour:', err);
                if (err.response?.status === 404) {
                    setError(t('tourNotFoundOrNotPublished'));
                } else {
                    setError(t('tourNotFound'));
                }
            } finally {
                setLoading(false);
            }
        };
        fetchTour();
    }, [id, t]);

    // Update Open Graph tags
    useEffect(() => {
        if (!tour) return;

        // Store original values to restore on unmount
        const originalTitle = document.title;

        // Update title
        const title = tour.og_title || tour.title;
        document.title = title;

        // Helper to update or create meta tag
        const updateMeta = (property, content) => {
            let element = document.querySelector(`meta[property="${property}"]`);
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute('property', property);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        const description = tour.og_description || tour.description || '';
        // Construct absolute URL for image if it's a relative path
        // Construct absolute URL for image (always use thumbnail as per requirement)
        let image = '';
        if (tour.thumbnail_path) {
            image = `${import.meta.env.VITE_API_URL || ''}/static/${tour.thumbnail_path}`;
        }
        const url = window.location.href;

        updateMeta('og:title', title);
        updateMeta('og:description', description);
        updateMeta('og:image', image);
        updateMeta('og:url', url);
        updateMeta('og:type', 'website');

        // Twitter cards
        const updateNameMeta = (name, content) => {
            let element = document.querySelector(`meta[name="${name}"]`);
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute('name', name);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        updateNameMeta('twitter:card', 'summary_large_image');
        updateNameMeta('twitter:title', title);
        updateNameMeta('twitter:description', description);
        updateNameMeta('twitter:image', image);

        return () => {
            document.title = originalTitle;
        };
    }, [tour]);

    if (loading) return <div className="flex justify-center items-center h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div></div>;
    if (error) return <div className="flex flex-col justify-center items-center h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50"><div className="text-danger-600 text-xl font-semibold mb-2">{error}</div><p className="text-gray-500">{t('tourErrorDescription')}</p></div>;
    if (!tour) return null;

    const isRTL = i18n.language.startsWith('ar');

    // Construct tour iframe URL
    // We need to find the entry file. Since the backend doesn't return the exact entry file path in the public endpoint yet,
    // we might need to rely on convention or fetch it.
    // However, the backend 'view_tour' logic was finding index.html.
    // We can expose the entry path in the public endpoint or just try standard paths.
    // Actually, let's update the backend to return the entry URL or path.
    // For now, let's assume standard structure or that we can browse to the folder.
    // Wait, if I use the static file server, I need the path.
    // The backend `view_tour` logic was: `url_base = base_path.replace("data/uploads", "uploads")`.
    // Let's assume the backend serves uploads at `/uploads`.
    // We need the relative path to index.html.
    // I should probably update the backend to return the `entry_url` in the public endpoint.

    // Parse property images safely
    const parsedPropertyImages = tour?.property_images ? (() => {
        try {
            const parsed = JSON.parse(tour.property_images);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error("Failed to parse property images", e);
            return [];
        }
    })() : [];
    const formatText = (text) => {
        if (!text) return null;
        // Split by markdown bold syntax **text**
        const parts = text.split(/(\*\*.*?\*\*)/g);
        return parts.map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={index}>{part.slice(2, -2)}</strong>;
            }
            return part;
        });
    };

    return (
        <div className="relative h-screen w-full overflow-hidden bg-gradient-to-br from-primary-900 via-secondary-900 to-accent-900">
            {/* Tour Iframe or Image Gallery */}
            {/* Show tour if: (1) entry_url exists AND (2) either no images OR viewMode is 'tour' */}
            {/* Tour Iframe or Image Gallery */}
            {/* Show tour if: (1) entry_url exists AND (2) either no images OR viewMode is 'tour' */}
            {tour.entry_url && (viewMode === 'tour' || parsedPropertyImages.length === 0) ? (
                <div className="absolute inset-0 w-full h-full bg-black">
                    {!iframeLoaded && (
                        <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/50 backdrop-blur-sm">
                            <div className="flex flex-col items-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
                                <p className="text-white font-medium animate-pulse">{t('loadingTour')}</p>
                            </div>
                        </div>
                    )}
                    <iframe
                        src={tour.entry_url.startsWith('http') ? tour.entry_url : `${import.meta.env.VITE_API_URL || ''}${tour.entry_url}`}
                        className={`absolute inset-0 w-full h-full border-0 transition-opacity duration-700 ${iframeLoaded ? 'opacity-100' : 'opacity-0'}`}
                        allowFullScreen
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; xr-spatial-tracking; magnetometer"
                        onLoad={() => setIframeLoaded(true)}
                    />
                </div>
            ) : parsedPropertyImages.length > 0 ? (
                <div className="absolute inset-0 w-full h-full overflow-y-auto bg-bg">
                    <div className="container mx-auto px-4 py-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {parsedPropertyImages.map((image, index) => (
                                <div
                                    key={index}
                                    className="relative aspect-square overflow-hidden rounded-lg shadow-lg cursor-pointer group"
                                    onClick={() => {
                                        setCurrentImageIndex(index);
                                        setLightboxOpen(true);
                                    }}
                                >
                                    <img
                                        src={`${import.meta.env.VITE_API_URL || ''}/static/${image}`}
                                        alt={`${tour.title} - Image ${index + 1}`}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                                        <svg className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                        </svg>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-text-muted text-xl">{t('noImagesAvailable')}</p>
                </div>
            )}

            {/* Info Overlay Toggle */}
            <button
                onClick={() => setShowInfo(!showInfo)}
                className={`absolute top-4 z-20 p-3 sm:p-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-full backdrop-blur-sm transition-all shadow-lg ${isRTL ? 'left-4' : 'right-4'} min-w-[44px] min-h-[44px] flex items-center justify-center`}
                title={showInfo ? t('hideInfo') : t('showInfo')}
            >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showInfo ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                </svg>
            </button>

            {/* Language Switcher - Positioned below info button */}
            <LanguageSwitcher align={isRTL ? 'left' : 'right'} className={`absolute top-16 z-20 ${isRTL ? 'left-4' : 'right-4'}`} />

            {/* Info Sidebar - Desktop: Side panel, Mobile: Bottom sheet */}
            <div className={`absolute bg-bg-glass backdrop-blur-md shadow-2xl transform transition-transform duration-300 z-10 overflow-y-auto border-r border-gold/20
                md:inset-y-0 md:w-80 lg:w-96 md:max-h-full
                bottom-0 left-0 right-0 max-h-[70vh] rounded-t-3xl md:rounded-none
                ${isRTL
                    ? 'md:right-0 md:left-auto border-l border-r-0'
                    : 'md:left-0 md:right-auto'
                }
                ${showInfo
                    ? 'translate-y-0 md:translate-y-0 md:translate-x-0'
                    : `translate-y-full md:translate-y-0 ${isRTL ? 'md:translate-x-full' : 'md:-translate-x-full'}`
                }`}>
                <div className="p-6 space-y-6">
                    {/* View Mode Toggle - Only show if both tour and images exist */}
                    {tour.entry_url && parsedPropertyImages.length > 0 && (
                        <div className="mb-4">
                            <div className="flex gap-2 p-1 bg-bg-card border border-gold/10 rounded-lg">
                                <button
                                    onClick={() => setViewMode('tour')}
                                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'tour'
                                        ? 'bg-gold text-text-invert shadow-md'
                                        : 'text-text hover:bg-bg-soft'
                                        }`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span>{t('viewTour')}</span>
                                    </div>
                                </button>
                                <button
                                    onClick={() => setViewMode('images')}
                                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'images'
                                        ? 'bg-gold text-text-invert shadow-md'
                                        : 'text-text hover:bg-bg-soft'
                                        }`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span>{t('viewImages')}</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Agency Header & Contact */}
                    <div className={`border-b border-gold/10 pb-6 mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {/* Clickable Identity */}
                        <a
                            href={`/agency/${tour.agency.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block mb-6 transition-transform hover:scale-[1.02] duration-300"
                        >
                            <div className="flex flex-col items-center">
                                {tour.agency.logo ? (
                                    <img
                                        src={`${import.meta.env.VITE_API_URL || ''}/static/agency_logos/${tour.agency.logo}`}
                                        alt={i18n.language.startsWith('ar') ? (tour.agency.name_ar || tour.agency.name) : (tour.agency.name_en || tour.agency.name)}
                                        className="h-24 w-24 object-contain mb-3"
                                    />
                                ) : (
                                    <div className="h-24 w-24 rounded-full bg-gradient-to-r from-primary-100 to-secondary-100 flex items-center justify-center text-primary-600 text-3xl font-bold mb-3 border-2 border-gold/20 group-hover:border-gold/50 transition-colors">
                                        {(i18n.language.startsWith('ar') ? (tour.agency.name_ar || tour.agency.name) : (tour.agency.name_en || tour.agency.name)).charAt(0)}
                                    </div>
                                )}
                            </div>
                            <h2 className="text-2xl font-bold text-gold text-center group-hover:text-gold-light transition-colors">
                                {i18n.language.startsWith('ar') ? (tour.agency.name_ar || tour.agency.name) : (tour.agency.name_en || tour.agency.name)}
                            </h2>
                            {tour.agency.city && <p className="text-sm text-textMuted text-center mt-1 group-hover:text-textPrimary transition-colors">{tour.agency.city}</p>}
                        </a>

                        {/* Contact Info (Moved Up) */}
                        <div className="space-y-3 px-2">
                            {/* View All Listings Button (Prominent) */}
                            <a
                                href={`/agency/${tour.agency.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full bg-gold-gradient text-white border-none py-3 rounded-xl font-bold uppercase tracking-wider shadow-lg hover:shadow-glow hover:scale-[1.02] transition-all flex items-center justify-center gap-2 mb-4"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                {t('viewAllListings')}
                            </a>

                            {tour.agency.phone && (
                                <a href={`tel:${tour.agency.phone}`} className="flex items-center gap-3 p-3 rounded-lg bg-bg-soft/50 hover:bg-bg-soft border border-transparent hover:border-gold/10 transition-all text-textPrimary group">
                                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                    </div>
                                    <span className="font-medium">{tour.agency.phone}</span>
                                </a>
                            )}

                            {tour.agency.website && (
                                <a href={tour.agency.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-lg bg-bg-soft/50 hover:bg-bg-soft border border-transparent hover:border-gold/10 transition-all text-textPrimary group">
                                    <div className="w-8 h-8 rounded-full bg-info-100 text-info-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                        </svg>
                                    </div>
                                    <span className="font-medium truncate">{t('website')}</span>
                                </a>
                            )}

                            {tour.agency.facebook && (
                                <a href={tour.agency.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-lg bg-bg-soft/50 hover:bg-bg-soft border border-transparent hover:border-gold/10 transition-all text-textPrimary group">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                        </svg>
                                    </div>
                                    <span className="font-medium">Facebook</span>
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Property Details */}
                    <div>
                        <h3 className={`text-sm font-bold text-gold uppercase tracking-wider mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('propertyDetails')}
                        </h3>
                        <div className="space-y-3">
                            {tour.show_price && (
                                <div className="flex justify-between">
                                    <span className="text-textMuted">{t('price')}</span>
                                    <span className="font-medium text-gold">
                                        {tour.price ? `${tour.price.toLocaleString()} ${t(`currency${tour.currency || defaultCurrency || 'USD'}`)}` : t('contactForPrice')}
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-textMuted">{t('propertyType')}</span>
                                <span className="font-medium text-textPrimary">
                                    {tour.property_type ? (
                                        propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase())
                                            ? (i18n.language === 'ar' 
                                                ? propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).ar 
                                                : (i18n.language === 'es' && propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).es 
                                                    ? propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).es 
                                                    : propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).en))
                                            : tour.property_type
                                    ) : '-'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-textMuted">{t('size')}</span>
                                <span className="font-medium text-textPrimary">{tour.size ? `${tour.size} m²` : '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-textMuted">{t('rooms')}</span>
                                <span className="font-medium text-textPrimary">{tour.rooms || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-textMuted">{t('floor')}</span>
                                <span className="font-medium text-textPrimary">{tour.floor || '-'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    {tour.description && (
                        <div>
                            <h3 className={`text-sm font-bold text-gold uppercase tracking-wider mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>{t('description')}</h3>
                            <p className="text-sm text-textMuted leading-relaxed whitespace-pre-wrap">{formatText(tour.description)}</p>
                        </div>
                    )}

                </div>
            </div>

            {/* Lightbox Modal */}
            {lightboxOpen && parsedPropertyImages.length > 0 && (
                <div
                    className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
                    onClick={() => setLightboxOpen(false)}
                >
                    {/* Close Button */}
                    <button
                        onClick={() => setLightboxOpen(false)}
                        className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-sm transition-all z-10 min-w-[44px] min-h-[44px] flex items-center justify-center"
                        title="Close"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Previous Button */}
                    {parsedPropertyImages.length > 1 && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setCurrentImageIndex((prev) =>
                                    prev === 0 ? parsedPropertyImages.length - 1 : prev - 1
                                );
                            }}
                            className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-sm transition-all min-w-[44px] min-h-[44px] flex items-center justify-center"
                            title="Previous"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                    )}

                    {/* Next Button */}
                    {parsedPropertyImages.length > 1 && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setCurrentImageIndex((prev) =>
                                    prev === parsedPropertyImages.length - 1 ? 0 : prev + 1
                                );
                            }}
                            className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-sm transition-all min-w-[44px] min-h-[44px] flex items-center justify-center"
                            title="Next"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    )}

                    {/* Image */}
                    <div
                        className="max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={`${import.meta.env.VITE_API_URL || ''}/static/${parsedPropertyImages[currentImageIndex]}`}
                            alt={`${tour.title} - Image ${currentImageIndex + 1}`}
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                        />
                    </div>

                    {/* Image Counter */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm">
                        {currentImageIndex + 1} / {parsedPropertyImages.length}
                    </div>
                </div>
            )}
        </div>
    );
}
